import {
  Badge, Box, Card, Chart, ErrorBoundary, IconName, Separator, Table, Text,
} from '@usealloy/component-library';
import type { BulletChartProps } from '@usealloy/component-library';
import { RiskRatingFormulaText } from './RiskRatingFormulaText';
import { riskTableColumns } from './constants';
import type { RiskFactor } from '../types';
import TagRuleModal from '../TagRuleModal';
import { useContext, useState } from 'react';
import { UserContext } from '~/dashboard/context';

export interface RiskRatingCardProps {
  entityToken: string;
  evaluationToken: string;
  workflowToken: string;
  workflowVersion: number;
  evaluationType: string;
  riskFactors: RiskFactor[];
  riskOutputFormula: Array<string | number>;
  riskRanges: BulletChartProps['colorRanges'];
  riskValue: number;
  riskValueColor: string;
  riskValueLabel: string;
  variant?: 'entityRisk' | 'overallRisk';
  canViewRiskFormula?: boolean;
}

export const RiskRatingCard = ({
  canViewRiskFormula = false,
  entityToken,
  evaluationToken,
  evaluationType,
  riskFactors,
  riskOutputFormula,
  riskRanges,
  riskValue,
  riskValueColor,
  riskValueLabel,
  variant = 'entityRisk',
  workflowToken,
  workflowVersion,
}: RiskRatingCardProps) => {
  const { featureFlags } = useContext(UserContext);

  const [isExplainabilityOpen, setIsExplainabilityOpen] = useState(false);
  const [explainabilityActionName, setExplainabilityActionName] = useState(null);

  const riskValueArray = typeof riskValue === 'number' ? [riskValue] : [];

  return (
    <>
      <Card
        collapsible
        header={(
          <Box
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Text size={4} weight={500}>Risk Score</Text>
            {riskValueLabel && (
              <Badge backgroundColor={riskValueColor} outline>{riskValueLabel}</Badge>
            )}
          </Box>
        )}
      >
        {riskRanges.length > 0 && (
          <ErrorBoundary
            bannerErrorText="The Risk Rating was unable to load..."
          >
            <Chart.Bullet colorRanges={riskRanges} points={riskValueArray} />
          </ErrorBoundary>
        )}
        {variant === 'entityRisk' && (
          <>
            <Table
              columns={riskTableColumns}
              data={riskFactors}
              error={!riskFactors.length}
              errorProps={{
                text: 'There are no categories available',
                iconName: IconName.DiamondExclamation,
              }}
              onRowClick={featureFlags.ruleExplainabilityPhase >= 4
                ? (event, { name }) => {
                  setExplainabilityActionName(name);
                  setIsExplainabilityOpen(true);
                } : undefined}
            />
            <Separator />
          </>
        )}
        <Box style={{ padding: 16 }}>
          <RiskRatingFormulaText
            riskFactors={riskFactors}
            riskOutputFormula={riskOutputFormula}
            riskValue={riskValue}
            canViewRiskFormula={canViewRiskFormula}
          />
        </Box>
      </Card>
      {isExplainabilityOpen && (
        <TagRuleModal
          actionName={explainabilityActionName}
          outcome={null}
          closeModal={() => {
            setIsExplainabilityOpen(false);
            setExplainabilityActionName(null);
          }}
          entityToken={entityToken}
          evaluationToken={evaluationToken}
          isPortfolioEvaluation={evaluationType === 'portfolio'}
          evaluationTags={[]}
          workflowToken={workflowToken}
          workflowVersion={workflowVersion}
        />
      )}
    </>
  );
};
